import type { PrecacheTracerMetricsMessage, SWMessage } from '@dvb/common';

import { Logger, SWMessageType } from '@dvb/common';
import { AnimateIcon, Show } from '@dvb/ui';
import {
  Button,
  cn,
  Modal,
  ModalBody,
  ModalContent,
  Progress,
  useDisclosure,
} from '@heroui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  hasUpdate: boolean;
  isForceUpdate?: boolean;
  updateServiceWorker: () => Promise<void>;
}

export function AppUpdateRequestModal({
  hasUpdate,
  isForceUpdate = false,
  updateServiceWorker,
}: Props) {
  const [isDownloading, setIsDownloading] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const { t } = useTranslation('ui');
  const { isOpen, onClose, onOpen, onOpenChange } = useDisclosure();
  const isOpenRef = useRef(isOpen); // For handleUpdate getting latest value

  const handleRefresh = useCallback(async () => {
    await updateServiceWorker();
    /**
     * Prevent not reloading
     * @see https://github.com/vite-pwa/vite-plugin-pwa/issues/583
     */
    window.location.reload();
  }, [updateServiceWorker]);

  const handleUpdate = useCallback(async () => {
    try {
      if (isOpenRef.current) {
        handleRefresh();
        return;
      }

      setIsDownloading(false);
      if (isForceUpdate) {
        handleRefresh();
        return;
      }

      onOpen();
    } catch (error) {
      onOpen();
      Logger.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOpen, handleRefresh]);

  const checkLatestUpdate = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        const handleUpdateFound = async () => {
          // Prevent first time access still popup the update modal
          if (!registration.active) return;

          registration.installing?.postMessage({
            type: SWMessageType.ENABLE_PRECACHE_TRACER,
          } satisfies SWMessage);

          navigator.serviceWorker.addEventListener(
            'message',
            (event: MessageEvent<PrecacheTracerMetricsMessage>) => {
              if (event.data.type === SWMessageType.PRECACHE_TRACER_METRICS) {
                const { current, total } = event.data;
                setPercentage(Math.round((current / total) * 100));
              }
            }
          );
          onOpen();
        };

        // Which means 'updatefound' event already triggered
        if (registration.installing) {
          handleUpdateFound();
          return;
        }

        registration.addEventListener('updatefound', handleUpdateFound);
      }
    }
  }, [onOpen]);

  useEffect(() => {
    if (hasUpdate) {
      handleUpdate();
    }
  }, [hasUpdate, handleUpdate]);

  // For handleUpdate getting latest value
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    checkLatestUpdate();
  }, [checkLatestUpdate]);

  return (
    <Modal
      backdrop="opaque"
      hideCloseButton
      // If enable force update, not allow to dismiss until updated (prevent deprecated API not working)
      isDismissable={isDownloading && !isForceUpdate}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="bottom"
      size="2xl"
    >
      <ModalContent>
        <ModalBody className="my-6">
          <div className="text-center">
            <AnimateIcon className="mx-auto scale-75" type="warning" />
            <Show
              fallback={
                <>
                  <h3 className="mb-4 text-lg">{t('newUpdateAvailable')}</h3>
                  <div className="flex justify-evenly">
                    <Button
                      className="mb-2"
                      color="primary"
                      onPress={handleRefresh}
                    >
                      <span className="truncate">{t('refresh')}</span>
                    </Button>
                    <Button
                      className="mb-2"
                      color="default"
                      onPress={() => onClose()}
                      variant="bordered"
                    >
                      <span className="truncate">{t('later')}</span>
                    </Button>
                  </div>
                </>
              }
              when={isDownloading}
            >
              <div className="mb-4">
                <h3 className="mb-2 text-lg">{t('fetchingLatestVersion')}</h3>
                <small className="text-default-500 text-sm">
                  {t('youMayDismissPopup')}
                </small>
              </div>
              <Progress
                aria-label={t('downloading')}
                className="my-3 w-full"
                classNames={{
                  labelWrapper: cn`mx-auto`,
                }}
                showValueLabel
                size="md"
                value={percentage}
              />
            </Show>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
